import { FormButtonBox } from "@/features/standard/Form/FormButtonBox";
import { FormMain } from "@/features/standard/Form/FormMain";
import { FormWrapper } from "@/features/standard/Form/FormWrapper";

import { mutateInventories } from "@/api/inventories/useInventoriesQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import customAlert from "@/features/ui/alert/alert";
import { ItemAutoComplete } from "@/features/ui/autoComplete/item/item-autoComplete";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { handleErrorResponse } from "@/utils/errorMessage";
import { Button, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import {
  DefaultApiWmsDeferredLotInfoPostRequest,
  WmsDeferredLotInfoPostRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const DeferredLotInfo = Object.assign({}, FormMain, {
  Button: Button,
  Input: TextInput,
  Date: DateInput,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  ItemAutoComplete: ItemAutoComplete,
});

export const DeferredLotInfoForm = (params: { formatterProps: any }) => {
  const { closeModal } = useModal();
  const { formatterProps } = params;
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { mutate: deferredLotInfoMutate } = useMutation(
    (params: DefaultApiWmsDeferredLotInfoPostRequest) =>
      mutateInventories
        .deferredLotInfo(params)
        .mutationFn(params as DefaultApiWmsDeferredLotInfoPostRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["inventories"]);
        closeModal(form.values);
        customAlert(
          `${t("inventory.successMessage", { item: `${t("inventory.deferredLotInfo")}` })}`,
          `${t("inventory.success", { item: `${t("inventory.deferredLotInfo")}` })}`,
          "green",
        );
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("inventory.deferredLotInfo") }),
          `${t("inventory.fail", { item: `${t("inventory.deferredLotInfo")}` })}`,
          "red",
        );
      },
    },
  );

  const form = useForm<Partial<WmsDeferredLotInfoPostRequest>>({
    initialValues: {
      itemCode: formatterProps.itemCode,
      locationCode: formatterProps.locationCode,
      quantity: formatterProps.quantity,
      lotId: formatterProps.lotId,
      lotInfo: {
        name: formatterProps.name,
        expiration: formatterProps.expiration,
      },
    },
  });

  const onSubmit = () => {
    try {
      const { itemCode, locationCode, quantity } = form.values;
      if (!itemCode && !locationCode && !quantity) return;

      deferredLotInfoMutate({
        wmsDeferredLotInfoPostRequest: {
          itemCode: formatterProps.itemCode || (form.values.itemCode as string),
          locationCode: formatterProps.locationCode || (form.values.locationCode as string),
          quantity: form.values.quantity as string,
          lotId: Number(formatterProps.lotId || form.values.lotId),
          lotInfo: {
            name: form.values.lotInfo?.name,
            expiration: form.values.lotInfo?.expiration,
          },
          actionFrom: "WEB",
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({ error, errorContext: t("inventory.deferredLotInfo") }),
        `${t("inventory.fail", { item: `${t("inventory.deferredLotInfo")}` })}`,
        "red",
      );
    }
  };

  return (
    <>
      <DeferredLotInfo.Wrapper>
        <DeferredLotInfo.ItemAutoComplete
          disabled
          label={t("item.code")}
          withAsterisk
          {...form.getInputProps("itemCode")}
          defaultValue={formatterProps?.itemCode}
        />
        <LocationAutoComplete
          label={t("location.code")}
          withAsterisk
          {...form.getInputProps("locationCode")}
          defaultValue={formatterProps?.locationCode}
        />
        <DeferredLotInfo.Input
          label={t("inventory.quantity")}
          withAsterisk
          type="number"
          {...form.getInputProps("quantity")}
          defaultValue={formatterProps?.quantity}
        />
        <DeferredLotInfo.Input
          label={t("lot.name")}
          {...form.getInputProps("lotInfo.name")}
          disabled={!formatterProps?.itemUsesLotName}
        />
        <DeferredLotInfo.Date
          label={t("lot.expiration")}
          //   placeholder="만료일을 선택하세요."
          minDate={new Date()}
          valueFormat="YYYY-MM-DD HH:mm:ss"
          {...form.getInputProps("lotInfo.expiration")}
          disabled={!formatterProps?.itemUsesLotExpiration}
        />
        <DeferredLotInfo.ButtonBox>
          <DeferredLotInfo.Button color="gray" onClick={closeModal}>
            {t("common.cancel")}
          </DeferredLotInfo.Button>
          <DeferredLotInfo.Button onClick={onSubmit}>
            {t("inventory.deferredLotInfo")}
          </DeferredLotInfo.Button>
        </DeferredLotInfo.ButtonBox>
      </DeferredLotInfo.Wrapper>
    </>
  );
};
